import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Hammer,
  Crosshair,
  Dumbbell,
  Flame,
  ChevronRight,
  ChevronLeft,
  Check,
} from "lucide-react";
import { toast } from "sonner";

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 1 | 2 | 3 | 4 | 5 | 6;

interface OnboardingData {
  heightFeet: string;
  heightInches: string;
  weightLbs: string;
  primaryGoal: "build_muscle" | "build_strength" | null;
  experienceLevel: "beginner" | "intermediate" | "advanced" | null;
  tracksMacros: boolean | null;
  targetProtein: string;
  targetCarbs: string;
  targetFat: string;
}

interface OnboardingFlowProps {
  onComplete: () => void;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Onboarding({ onComplete }: OnboardingFlowProps) {
  const [step, setStep] = useState<Step>(1);
  const [data, setData] = useState<OnboardingData>({
    heightFeet: "",
    heightInches: "",
    weightLbs: "",
    primaryGoal: null,
    experienceLevel: null,
    tracksMacros: null,
    targetProtein: "",
    targetCarbs: "",
    targetFat: "",
  });

  const completeOnboarding = trpc.profile.completeOnboarding.useMutation({
    onSuccess: () => {
      onComplete();
    },
    onError: (err) => {
      toast.error("Something went wrong: " + err.message);
    },
  });

  const totalSteps = 6;
  const progress = ((step - 1) / (totalSteps - 1)) * 100;

  const canAdvance = (): boolean => {
    if (step === 2) return true; // height/weight optional
    if (step === 3) return data.primaryGoal !== null;
    if (step === 4) return data.experienceLevel !== null;
    if (step === 5) return data.tracksMacros !== null;
    return true;
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep((s) => (s + 1) as Step);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (step > 1) setStep((s) => (s - 1) as Step);
  };

  const handleSkip = () => {
    completeOnboarding.mutate(undefined);
  };

  const handleFinish = () => {
    const trainingMode = data.primaryGoal === "build_strength" ? "overload" : "hypertrophy";
    const fitnessGoal = data.primaryGoal === "build_strength" ? "increase_strength" : "build_muscle";

    const payload: Record<string, unknown> = {
      fitnessGoal,
      primaryTrainingMode: trainingMode,
      ...(data.experienceLevel ? { experienceLevel: data.experienceLevel } : {}),
    };

    const hFeet = parseInt(data.heightFeet);
    const hInches = parseInt(data.heightInches);
    const wLbs = parseFloat(data.weightLbs);
    if (!isNaN(hFeet) && hFeet > 0) payload.heightFeet = hFeet;
    if (!isNaN(hInches) && data.heightInches !== "") payload.heightInches = hInches;
    if (!isNaN(wLbs) && wLbs > 0) payload.initialWeightLbs = wLbs;

    if (data.tracksMacros) {
      const p = parseInt(data.targetProtein);
      const c = parseInt(data.targetCarbs);
      const f = parseInt(data.targetFat);
      if (!isNaN(p) && p > 0) payload.targetProtein = p;
      if (!isNaN(c) && c > 0) payload.targetCarbs = c;
      if (!isNaN(f) && f > 0) payload.targetFat = f;
      if (!isNaN(p) && !isNaN(c) && !isNaN(f)) {
        payload.targetCalories = p * 4 + c * 4 + f * 9;
      }
    }

    completeOnboarding.mutate(payload as Parameters<typeof completeOnboarding.mutate>[0]);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black overflow-hidden">
      {/* Skip button */}
      <div className="flex justify-end px-5 pt-5 shrink-0">
        <button
          onClick={handleSkip}
          disabled={completeOnboarding.isPending}
          className="font-mono text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          Skip
        </button>
      </div>

      {/* Progress bar */}
      <div className="px-6 pt-3 pb-2 shrink-0">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest">
            Step {step} of {totalSteps}
          </span>
          <span className="text-[10px] font-mono text-zinc-600">{Math.round(progress)}%</span>
        </div>
        <div className="h-0.5 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#4ade80] rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {step === 1 && <StepWelcome />}
        {step === 2 && <StepHeightWeight data={data} setData={setData} />}
        {step === 3 && <StepPrimaryGoal data={data} setData={setData} />}
        {step === 4 && <StepExperience data={data} setData={setData} />}
        {step === 5 && <StepFuelBaseline data={data} setData={setData} />}
        {step === 6 && <StepConfirmation data={data} />}
      </div>

      {/* Navigation */}
      <div className="px-6 pb-10 flex items-center gap-3 shrink-0">
        {step > 1 && (
          <Button
            variant="outline"
            onClick={handleBack}
            className="flex-1 font-mono uppercase tracking-widest text-xs border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 bg-transparent"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        )}
        <Button
          onClick={handleNext}
          disabled={!canAdvance() || completeOnboarding.isPending}
          className="flex-1 h-12 font-mono text-sm font-bold rounded-xl transition-all active:scale-[0.97] bg-[#4ade80] text-black hover:bg-[#22c55e] border-none"
        >
          {step === totalSteps ? (
            completeOnboarding.isPending ? "Saving..." : (
              <span className="flex items-center gap-2"><Check className="h-4 w-4" /> Enter The Forge</span>
            )
          ) : (
            <span className="flex items-center gap-2">
              {step === 1 ? "Let's Go" : "Next"} <ChevronRight className="h-4 w-4" />
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}

// ─── Step 1: Welcome ──────────────────────────────────────────────────────────

function StepWelcome() {
  return (
    <div className="flex flex-col items-center justify-center min-h-full py-8 space-y-8">
      <div className="w-24 h-24 rounded-2xl bg-[#4ade80]/10 border border-[#4ade80]/20 flex items-center justify-center">
        <Hammer className="h-12 w-12 text-[#4ade80]" />
      </div>

      <div className="text-center space-y-3">
        <p className="font-mono text-xs uppercase tracking-widest text-[#4ade80]">Welcome</p>
        <h1 className="font-mono text-3xl font-bold text-white leading-tight">Welcome to Forge</h1>
        <p className="text-zinc-400 text-sm leading-relaxed max-w-xs mx-auto">
          Let's set up your training profile. This takes about 60 seconds and helps Forge calibrate your experience from day one.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3 w-full max-w-xs">
        {[
          { icon: Crosshair, label: "Precision Tracking" },
          { icon: Dumbbell, label: "Smart Programming" },
          { icon: Flame, label: "Fuel Baseline" },
        ].map(({ icon: Icon, label }) => (
          <div key={label} className="bg-zinc-900 rounded-xl p-3 text-center space-y-1.5 border border-zinc-800">
            <Icon className="h-5 w-5 mx-auto text-[#4ade80]" />
            <p className="text-[10px] font-mono text-zinc-500 leading-tight">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Step 2: Height & Weight ──────────────────────────────────────────────────

function StepHeightWeight({
  data,
  setData,
}: {
  data: OnboardingData;
  setData: React.Dispatch<React.SetStateAction<OnboardingData>>;
}) {
  return (
    <div className="space-y-6 py-4">
      <div className="space-y-1">
        <p className="font-mono text-xs uppercase tracking-widest text-[#4ade80]">Step 2</p>
        <h2 className="font-mono text-2xl font-bold text-white">Height & Weight</h2>
        <p className="text-zinc-400 text-sm">Used to calculate your baseline metrics. You can update these anytime in Form.</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-[11px] font-mono text-zinc-500 uppercase mb-2 tracking-widest">Height</label>
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <Input
                type="number"
                placeholder="5"
                min={3}
                max={8}
                value={data.heightFeet}
                onChange={(e) => setData((d) => ({ ...d, heightFeet: e.target.value }))}
                className="bg-zinc-900 border-zinc-700 text-white font-mono pr-10 focus:border-[#4ade80]"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500 font-mono">ft</span>
            </div>
            <div className="relative">
              <Input
                type="number"
                placeholder="10"
                min={0}
                max={11}
                value={data.heightInches}
                onChange={(e) => setData((d) => ({ ...d, heightInches: e.target.value }))}
                className="bg-zinc-900 border-zinc-700 text-white font-mono pr-10 focus:border-[#4ade80]"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500 font-mono">in</span>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-mono text-zinc-500 uppercase mb-2 tracking-widest">Body Weight</label>
          <div className="relative">
            <Input
              type="number"
              step="0.1"
              placeholder="185.0"
              value={data.weightLbs}
              onChange={(e) => setData((d) => ({ ...d, weightLbs: e.target.value }))}
              className="bg-zinc-900 border-zinc-700 text-white font-mono pr-12 focus:border-[#4ade80]"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500 font-mono">lbs</span>
          </div>
        </div>
      </div>

      <p className="text-[11px] text-zinc-600 font-mono text-center">Optional — tap Next to skip</p>
    </div>
  );
}

// ─── Step 3: Primary Goal ─────────────────────────────────────────────────────

function StepPrimaryGoal({
  data,
  setData,
}: {
  data: OnboardingData;
  setData: React.Dispatch<React.SetStateAction<OnboardingData>>;
}) {
  const goals = [
    {
      value: "build_muscle" as const,
      label: "Build Muscle",
      mode: "Hypertrophy Mode",
      description: "Volume-focused training. Rep ranges 8–15. Progressive overload through sets and reps.",
      emoji: "💪",
    },
    {
      value: "build_strength" as const,
      label: "Build Strength",
      mode: "Overload Mode",
      description: "Intensity-focused training. Rep ranges 1–6. Progressive overload through weight.",
      emoji: "🏋️",
    },
  ];

  return (
    <div className="space-y-6 py-4">
      <div className="space-y-1">
        <p className="font-mono text-xs uppercase tracking-widest text-[#4ade80]">Step 3</p>
        <h2 className="font-mono text-2xl font-bold text-white">Primary Goal</h2>
        <p className="text-zinc-400 text-sm">This sets your default training mode. You can change it per session in Forge.</p>
      </div>

      <div className="space-y-3">
        {goals.map((goal) => (
          <button
            key={goal.value}
            onClick={() => setData((d) => ({ ...d, primaryGoal: goal.value }))}
            className="w-full text-left p-4 rounded-xl border-2 transition-all duration-200 active:scale-[0.98]"
            style={{
              borderColor: data.primaryGoal === goal.value ? "#4ade80" : "#3f3f46",
              background: data.primaryGoal === goal.value ? "rgba(74,222,128,0.08)" : "rgba(39,39,42,0.5)",
            }}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{goal.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-bold text-sm text-white">{goal.label}</span>
                  <span className="text-[10px] font-mono text-[#4ade80] bg-[#4ade80]/10 px-2 py-0.5 rounded-full">{goal.mode}</span>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">{goal.description}</p>
              </div>
              {data.primaryGoal === goal.value && (
                <Check className="h-4 w-4 text-[#4ade80] flex-shrink-0 mt-0.5" />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Step 4: Experience Level ─────────────────────────────────────────────────

function StepExperience({
  data,
  setData,
}: {
  data: OnboardingData;
  setData: React.Dispatch<React.SetStateAction<OnboardingData>>;
}) {
  const levels = [
    { value: "beginner" as const, label: "Beginner", description: "Under 1 year of consistent training", icon: "🌱" },
    { value: "intermediate" as const, label: "Intermediate", description: "1–3 years of consistent training", icon: "⚡" },
    { value: "advanced" as const, label: "Advanced", description: "3+ years, familiar with periodization", icon: "🔥" },
  ];

  return (
    <div className="space-y-6 py-4">
      <div className="space-y-1">
        <p className="font-mono text-xs uppercase tracking-widest text-[#4ade80]">Step 4</p>
        <h2 className="font-mono text-2xl font-bold text-white">Experience Level</h2>
        <p className="text-zinc-400 text-sm">Helps calibrate plateau detection and recovery recommendations.</p>
      </div>

      <div className="space-y-3">
        {levels.map((level) => (
          <button
            key={level.value}
            onClick={() => setData((d) => ({ ...d, experienceLevel: level.value }))}
            className="w-full text-left p-4 rounded-xl border-2 transition-all duration-200 active:scale-[0.98]"
            style={{
              borderColor: data.experienceLevel === level.value ? "#4ade80" : "#3f3f46",
              background: data.experienceLevel === level.value ? "rgba(74,222,128,0.08)" : "rgba(39,39,42,0.5)",
            }}
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">{level.icon}</span>
              <div className="flex-1">
                <p className="font-bold text-sm text-white">{level.label}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{level.description}</p>
              </div>
              {data.experienceLevel === level.value && (
                <Check className="h-4 w-4 text-[#4ade80] flex-shrink-0" />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Step 5: Fuel Baseline ────────────────────────────────────────────────────

function StepFuelBaseline({
  data,
  setData,
}: {
  data: OnboardingData;
  setData: React.Dispatch<React.SetStateAction<OnboardingData>>;
}) {
  return (
    <div className="space-y-6 py-4">
      <div className="space-y-1">
        <p className="font-mono text-xs uppercase tracking-widest text-[#4ade80]">Step 5</p>
        <h2 className="font-mono text-2xl font-bold text-white">Fuel Baseline</h2>
        <p className="text-zinc-400 text-sm">Do you already track your macros?</p>
      </div>

      <div className="space-y-3">
        {[
          { value: true, label: "Yes, I track macros", sub: "I'll enter my current targets" },
          { value: false, label: "No, skip for now", sub: "I can set targets later in Fuel" },
        ].map(({ value, label, sub }) => (
          <button
            key={String(value)}
            onClick={() => setData((d) => ({ ...d, tracksMacros: value }))}
            className="w-full text-left p-4 rounded-xl border-2 transition-all duration-200 active:scale-[0.98]"
            style={{
              borderColor: data.tracksMacros === value ? "#4ade80" : "#3f3f46",
              background: data.tracksMacros === value ? "rgba(74,222,128,0.08)" : "rgba(39,39,42,0.5)",
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-sm text-white">{label}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{sub}</p>
              </div>
              {data.tracksMacros === value && <Check className="h-4 w-4 text-[#4ade80]" />}
            </div>
          </button>
        ))}
      </div>

      {data.tracksMacros === true && (
        <div className="space-y-3 pt-2 border-t border-zinc-800">
          <p className="text-[11px] font-mono text-zinc-500 uppercase tracking-widest">Daily Macro Targets</p>
          {[
            { key: "targetProtein" as const, label: "Protein" },
            { key: "targetCarbs" as const, label: "Carbs" },
            { key: "targetFat" as const, label: "Fat" },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-[11px] font-mono text-zinc-500 mb-1">{label}</label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder="0"
                  min={0}
                  value={data[key]}
                  onChange={(e) => setData((d) => ({ ...d, [key]: e.target.value }))}
                  className="bg-zinc-900 border-zinc-700 text-white font-mono pr-8 focus:border-[#4ade80]"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500 font-mono">g</span>
              </div>
            </div>
          ))}
          {(() => {
            const p = parseInt(data.targetProtein) || 0;
            const c = parseInt(data.targetCarbs) || 0;
            const f = parseInt(data.targetFat) || 0;
            const cals = p * 4 + c * 4 + f * 9;
            if (cals > 0) {
              return (
                <div className="bg-[#4ade80]/10 border border-[#4ade80]/20 rounded-lg p-3 text-center">
                  <span className="text-xs font-mono text-zinc-400">Estimated Daily Calories: </span>
                  <span className="font-bold text-[#4ade80] font-mono">{cals} kcal</span>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}
    </div>
  );
}

// ─── Step 6: Confirmation ─────────────────────────────────────────────────────

function StepConfirmation({ data }: { data: OnboardingData }) {
  const modeLabel = data.primaryGoal === "build_strength" ? "Overload" : "Hypertrophy";
  const modeDesc =
    data.primaryGoal === "build_strength"
      ? "Strength-first. Progressive overload through weight."
      : "Volume-first. Progressive overload through reps and sets.";

  return (
    <div className="flex flex-col items-center py-8 space-y-6">
      <div className="w-20 h-20 rounded-2xl bg-[#4ade80]/10 border border-[#4ade80]/20 flex items-center justify-center">
        <Check className="h-10 w-10 text-[#4ade80]" />
      </div>

      <div className="text-center space-y-2">
        <p className="font-mono text-xs uppercase tracking-widest text-[#4ade80]">Ready</p>
        <h2 className="font-mono text-2xl font-bold text-white">Forge is Calibrated</h2>
        <p className="text-zinc-400 text-sm">Here's your training configuration:</p>
      </div>

      <div className="space-y-3 w-full max-w-sm">
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
          <p className="text-[11px] font-mono text-zinc-500 uppercase tracking-widest mb-1">Training Mode</p>
          <p className="font-bold text-[#4ade80]">{modeLabel} Mode</p>
          <p className="text-xs text-zinc-400 mt-1">{modeDesc}</p>
        </div>

        {data.experienceLevel && (
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
            <p className="text-[11px] font-mono text-zinc-500 uppercase tracking-widest mb-1">Experience Level</p>
            <p className="font-bold text-white capitalize">{data.experienceLevel}</p>
          </div>
        )}

        {data.tracksMacros && (data.targetProtein || data.targetCarbs || data.targetFat) && (
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
            <p className="text-[11px] font-mono text-zinc-500 uppercase tracking-widest mb-2">Fuel Targets</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                { label: "Protein", value: data.targetProtein },
                { label: "Carbs", value: data.targetCarbs },
                { label: "Fat", value: data.targetFat },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="font-bold font-mono text-white">{value || "—"}g</p>
                  <p className="text-[10px] text-zinc-500">{label}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <p className="text-[11px] text-center text-zinc-600 font-mono">
        You can update any of these in Forge and Loadout.
      </p>
    </div>
  );
}
